import { useMemo, useState } from 'react';

// One local-state-only, genuinely-playable preview per Grid game type,
// modeled directly on how the real student-facing `quiz` webapp plays each
// one (Table.jsx/Quiz.jsx/Word.jsx/MatchingGaps.jsx) - built fresh for this
// codebase (own components, own CSS vars, no ported code), but matching
// the actual mechanic and content mapping per type rather than inventing a
// generic substitute. Nothing here is graded or persisted.

function shuffle(list) {
    const copy = [...list];
    for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
    }
    return copy;
}

// --- type 1 flip-card mechanic: click to 3D-flip and reveal the back face
// - exactly Table.jsx's own interaction, just restyled to this app's dark
// theme instead of ported. Front shows the card's position, back reveals
// its value (Table.jsx's own front=index/back=value mapping). ---
function FlipCard({ front, back }) {
    const [flipped, setFlipped] = useState(false);
    return (
        <div className='course-preview-flip-card' onClick={() => setFlipped((prev) => !prev)}>
            <div className={'course-preview-flip-card-inner' + (flipped ? ' flipped' : '')}>
                <div className='course-preview-flip-card-front'>{front}</div>
                <div className='course-preview-flip-card-back'>{back}</div>
            </div>
        </div>
    );
}
export function NumberGridPreview({ cards }) {
    return (
        <div className='course-preview-flip-grid'>
            {cards.map((c, i) => <FlipCard key={c.id} front={i} back={c.value} />)}
        </div>
    );
}

// --- type 2 "Квіз" is NOT a flashcard - it's a fill-the-gap check. On this
// Grid type the field naming is inverted from what it looks like (a quirk
// of GridCard's model being reused across all 4 types): `answer` holds the
// question text (usually containing a `___`-style gap), `value` holds the
// single correct fill word - confirmed against real production data, incl.
// cases where `value` is legitimately empty (open-ended personal-response
// questions with no single correct word, e.g. "What are you afraid of?"),
// in which case this renders as a free-response field with no check UI
// rather than a pointless always-wrong comparison. ---
const GAP_MARKER_RE = /_+/;

function QuizGapCard({ card }) {
    const question = card.answer || '';
    const correctWord = (card.value || '').trim();
    const hasAnswer = correctWord.length > 0;
    const gapMatch = question.match(GAP_MARKER_RE);
    const [typed, setTyped] = useState('');
    const [checked, setChecked] = useState(false);
    const isCorrect = checked && typed.trim().toLowerCase() === correctWord.toLowerCase();

    const handleChange = (e) => {
        setTyped(e.target.value);
        setChecked(false);
    };
    const inputClass = 'course-preview-blank-input course-preview-quiz-gap-input' + (checked ? (isCorrect ? ' correct' : ' wrong') : '');

    return (
        <div className='course-preview-quiz-gap-card'>
            <p className='course-preview-question-text'>
                {gapMatch ? (
                    <>
                        {question.slice(0, gapMatch.index)}
                        <input type='text' className={inputClass} value={typed} onChange={handleChange} />
                        {question.slice(gapMatch.index + gapMatch[0].length)}
                    </>
                ) : question}
            </p>
            {!gapMatch ? (
                <input type='text' className={inputClass} placeholder='Впишіть слово...' value={typed} onChange={handleChange} />
            ) : ''}
            {hasAnswer ? (
                <div className='course-preview-quiz-gap-actions'>
                    <button type='button' className='course-preview-quiz-check-btn' disabled={!typed.trim()} onClick={() => setChecked(true)}>Перевірити</button>
                    {checked ? (
                        isCorrect
                            ? <span className='course-preview-quiz-gap-feedback correct'>Правильно!</span>
                            : <span className='course-preview-quiz-gap-feedback wrong'>Правильна відповідь: {correctWord}</span>
                    ) : ''}
                </div>
            ) : ''}
        </div>
    );
}
export function QuizCardsPreview({ cards }) {
    return <div className='course-preview-quiz-gap-list'>{cards.map((c) => <QuizGapCard card={c} key={c.id} />)}</div>;
}

// --- type 3 "Слова": tap a shuffled letter into the next empty slot; tap
// a placed letter to send it back to the pool. Every placed slot shows
// live per-position correct/wrong feedback (Word.jsx's own mechanic) -
// safe to reveal since the pool tiles already ARE the word's own letters,
// nothing hidden gets leaked by the color feedback. ---
function WordCard({ card }) {
    const word = card.value || '';
    const correctLetters = useMemo(() => word.split(''), [word]);
    const [pool] = useState(() => shuffle(word.split('').map((ch, i) => ({ ch, key: i + '-' + ch }))));
    const [slots, setSlots] = useState(() => Array(correctLetters.length).fill(null));
    const usedKeys = slots.filter(Boolean).map((s) => s.key);
    const isComplete = slots.length > 0 && slots.every(Boolean);

    const placeLetter = (tile) => {
        const emptyIndex = slots.findIndex((s) => s === null);
        if (emptyIndex === -1) return;
        setSlots((prev) => {
            const next = [...prev];
            next[emptyIndex] = tile;
            return next;
        });
    };
    const removeLetter = (index) => {
        setSlots((prev) => {
            const next = [...prev];
            next[index] = null;
            return next;
        });
    };

    return (
        <div className='course-preview-word-card'>
            <div className='course-preview-word-slots'>
                {slots.map((slot, i) => {
                    const state = slot ? (slot.ch.toLowerCase() === correctLetters[i].toLowerCase() ? ' correct' : ' wrong') : '';
                    return (
                        <div key={i} className={'course-preview-word-slot' + state} onClick={() => slot && removeLetter(i)}>
                            {slot ? slot.ch.toUpperCase() : ''}
                        </div>
                    );
                })}
                {isComplete ? (
                    <span className='course-preview-word-reset' title='Скинути' onClick={() => setSlots(Array(correctLetters.length).fill(null))}>↺</span>
                ) : ''}
            </div>
            <div className='course-preview-word-pool'>
                {pool.map((t) => (
                    <div key={t.key} className={'course-preview-word-tile' + (usedKeys.includes(t.key) ? ' used' : '')} onClick={() => !usedKeys.includes(t.key) && placeLetter(t)}>
                        {t.ch.toUpperCase()}
                    </div>
                ))}
            </div>
        </div>
    );
}
export function WordBuilderPreview({ cards }) {
    return <div className='course-preview-word-list'>{cards.map((c) => <WordCard card={c} key={c.id} />)}</div>;
}

// --- type 4 "Підбір пар": real drag-and-drop, matching MatchingGaps.jsx's
// own mechanic and its own data convention (confirmed against live data,
// not guessed) - the first card (by id) has an empty `value` and holds the
// whole word bank space-separated in its `answer`; every following card is
// a real gap sentence (`value` contains a run of underscores) whose own
// `answer` is the single correct fill word. Falls back to building the
// bank from every card's own answer if that convention isn't followed. ---
function GapFillGame({ cards }) {
    const sorted = useMemo(() => [...cards].sort((a, b) => a.id - b.id), [cards]);
    const hasBankCard = sorted.length > 0 && !sorted[0].value;
    const bankCard = hasBankCard ? sorted[0] : null;
    const sentenceCards = hasBankCard ? sorted.slice(1) : sorted;
    const bankWords = useMemo(() => {
        if (bankCard && bankCard.answer) return bankCard.answer.trim().split(/\s+/).filter(Boolean);
        return sentenceCards.map((c) => c.answer).filter(Boolean);
    }, [bankCard, sentenceCards]);

    const [usedWords, setUsedWords] = useState({});
    const [dragWord, setDragWord] = useState(null);
    const [dragOverId, setDragOverId] = useState(null);
    const [wrongId, setWrongId] = useState(null);
    const placedWordSet = new Set(Object.values(usedWords));

    const handleDrop = (sentenceCard) => {
        setDragOverId(null);
        if (!dragWord || usedWords[sentenceCard.id]) return;
        const correct = (sentenceCard.answer || '').trim().toLowerCase() === dragWord.trim().toLowerCase();
        if (correct) {
            setUsedWords((prev) => ({ ...prev, [sentenceCard.id]: dragWord }));
        } else {
            setWrongId(sentenceCard.id);
            setTimeout(() => setWrongId(null), 400);
        }
        setDragWord(null);
    };

    const renderSentence = (sentenceCard) => {
        const filled = usedWords[sentenceCard.id];
        const gapMatch = (sentenceCard.value || '').match(/_+/);
        if (filled && gapMatch) {
            const parts = sentenceCard.value.split(gapMatch[0]);
            return <>{parts[0]}<strong className='course-preview-gap-filled'>{filled}</strong>{parts[1]}</>;
        }
        return sentenceCard.value;
    };

    return (
        <div className='course-preview-gapfill'>
            <div className='course-preview-gapfill-bank'>
                {bankWords.map((w, i) => (
                    <div key={i} draggable={!placedWordSet.has(w)}
                        className={'course-preview-gapfill-chip' + (placedWordSet.has(w) ? ' used' : '')}
                        onDragStart={() => setDragWord(w)} onDragEnd={() => setDragWord(null)}>
                        {w}
                    </div>
                ))}
            </div>
            <div className='course-preview-gapfill-sentences'>
                {sentenceCards.map((c, i) => (
                    <div key={c.id}
                        className={'course-preview-gapfill-sentence' + (usedWords[c.id] ? ' filled' : '') + (dragOverId === c.id ? ' dragover' : '') + (wrongId === c.id ? ' wrong' : '')}
                        onDragOver={(e) => { e.preventDefault(); setDragOverId(c.id); }}
                        onDragLeave={() => setDragOverId((prev) => (prev === c.id ? null : prev))}
                        onDrop={(e) => { e.preventDefault(); handleDrop(c); }}>
                        <span className='course-preview-gapfill-index'>{i + 1}.</span>
                        <span className='course-preview-gapfill-text'>{renderSentence(c)}</span>
                        {usedWords[c.id] ? <span className='course-preview-gapfill-check'>✓</span> : ''}
                    </div>
                ))}
            </div>
        </div>
    );
}
export function MatchingPairsPreview({ cards }) {
    return <GapFillGame cards={cards} />;
}

export const GRID_TYPE_PREVIEWS = {
    1: NumberGridPreview,
    2: QuizCardsPreview,
    3: WordBuilderPreview,
    4: MatchingPairsPreview,
};
